import {
  type UseQueryOptions,
  queryOptions as queryOptionsTanStack,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDebounce } from '@uidotdev/usehooks';
import { cn } from 'cn';
import ms from 'ms';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';

import { Spinner } from '../Spinner';

type PageData<TItem> = {
  data: TItem[];
  hasNextPage: boolean;
  totalCount: number;
};

type P<TItem> = {
  readonly debounceMs?: number;
  readonly fetchPage: (
    offset: number,
    signal: AbortSignal,
  ) => Promise<PageData<TItem>>;
  readonly hasDivider?: boolean;
  readonly itemHeight?: number;
  readonly onLoadingChange?: (isLoading: boolean) => void;
  readonly overscan?: number;
  readonly pageSize?: number;
  /**
   * Override any TanStack Query options for the per-page queries.
   * `queryKey` and `queryFn` are managed internally and cannot be overridden.
   * Example: `{ staleTime: 0, gcTime: 30_000 }`
   */
  readonly queryOptions?: Omit<
    UseQueryOptions<PageData<TItem>>,
    'queryFn' | 'queryKey'
  >;
  readonly queryKey: readonly unknown[];
  readonly renderItem: (item: TItem) => ReactNode;
  readonly renderLoadingItem?: (index: number) => ReactNode;
  readonly totalCount?: number;
} & ComponentStyling;

type PageQueryKeyParams<TItem> = {
  readonly fetchPage: P<TItem>['fetchPage'];
  readonly pageIndex: number;
  readonly pageSize: number;
  readonly queryKey: readonly unknown[];
};

/**
 * Builds the cache key of a single page query of `VirtualizedListServerDriven`.
 *
 * Exported so a parent that needs page 0 before the list mounts — for a total
 * count, an empty state, or a counter — can observe that very query instead of
 * fetching offset 0 again under a key of its own.
 */
export function getVirtualizedListPageQueryKey<TItem>({
  fetchPage,
  pageIndex,
  pageSize,
  queryKey,
}: PageQueryKeyParams<TItem>): readonly unknown[] {
  return [...queryKey, 'page', pageIndex, fetchPage, pageSize];
}

function getPageQueryOptions<TItem>({
  fetchPage,
  pageIndex,
  pageSize,
  queryKey,
  queryOptions,
}: PageQueryKeyParams<TItem> & Pick<P<TItem>, 'queryOptions'>) {
  return queryOptionsTanStack({
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchPage(pageIndex * pageSize, signal),
    queryKey: getVirtualizedListPageQueryKey({
      fetchPage,
      pageIndex,
      pageSize,
      queryKey,
    }),
    staleTime: ms('60s'),
    ...queryOptions,
  });
}

/**
 * VirtualizedListServerDriven
 *
 * A virtualized list component that fetches data in a windowed/sparse manner.
 * Only fetches the pages containing currently visible items, allowing users
 * to jump to any position without loading all intermediate data.
 *
 * Features:
 * - Only loads pages for visible items (sparse loading)
 * - Debounced fetching to avoid excessive requests during rapid scrolling
 * - Shows loading placeholders for items being fetched
 * - Caches previously loaded pages
 */
export const VirtualizedListServerDriven = <TItem,>({
  queryKey,
  ...props
}: P<TItem>) => {
  const serializedQueryKey = JSON.stringify(queryKey);

  return (
    <VirtualizedListServerDrivenContent
      key={serializedQueryKey}
      queryKey={queryKey}
      {...props}
    />
  );
};

const INITIAL_PAGE_RANGE = {
  startPage: 0,
  endPage: 0,
};

const VirtualizedListServerDrivenContent = <TItem,>({
  className = 'h-96',
  debounceMs = 150,
  fetchPage,
  hasDivider = true,
  itemHeight = 48,
  onLoadingChange,
  overscan = 10,
  pageSize = 100,
  queryKey,
  queryOptions,
  renderItem,
  renderLoadingItem,
  style,
  totalCount: totalCountProp,
}: P<TItem>) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const { data: firstPageData, isLoading: isFirstPageLoading } = useQuery(
    getPageQueryOptions({
      fetchPage,
      pageIndex: 0,
      pageSize,
      queryKey,
      queryOptions,
    }),
  );
  const totalCount = totalCountProp ?? firstPageData?.totalCount ?? 0;

  const rowVirtualizer = useVirtualizer({
    count: totalCount,
    estimateSize: () => itemHeight,
    getScrollElement: () => parentRef.current,
    overscan,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const visiblePageRange = useMemo(() => {
    if (virtualItems.length === 0 || totalCount === 0) {
      return INITIAL_PAGE_RANGE;
    }

    const firstIndex = virtualItems[0]?.index ?? 0;
    const lastIndex = virtualItems.at(-1)?.index ?? 0;

    return {
      startPage: Math.floor(firstIndex / pageSize),
      endPage: Math.floor(lastIndex / pageSize),
    };
  }, [virtualItems, pageSize, totalCount]);

  // Debounce to avoid rapid fetching during scroll
  const debouncedPageRange = useDebounce(visiblePageRange, debounceMs);

  // Page 0 is fetched separately so totalCount is available before virtualizing.
  const pageIndices = useMemo(() => {
    const pages = new Set<number>();
    for (
      let i = debouncedPageRange.startPage;
      i <= debouncedPageRange.endPage;
      i++
    ) {
      if (i !== 0) {
        pages.add(i);
      }
    }
    return Array.from(pages).sort((a, b) => a - b);
  }, [debouncedPageRange]);

  // Fetch only the visible pages beyond the first page.
  const pageQueries = useQueries({
    queries: pageIndices.map((pageIndex) =>
      getPageQueryOptions({
        fetchPage,
        pageIndex,
        pageSize,
        queryKey,
        queryOptions,
      }),
    ),
  });

  const itemsMap = new Map<number, TItem>();

  firstPageData?.data.forEach((item, itemIndex) => {
    itemsMap.set(itemIndex, item);
  });

  pageQueries.forEach((query, queryArrayIndex) => {
    if (query.data) {
      const pageIndex = pageIndices[queryArrayIndex];
      query.data.data.forEach((item, itemIndex) => {
        itemsMap.set(pageIndex * pageSize + itemIndex, item);
      });
    }
  });

  // Track loading state for initial data and subsequent pages
  const hasInitialData = firstPageData !== undefined;
  const isInitialLoading = !hasInitialData && isFirstPageLoading;
  const isLoadingMorePages =
    hasInitialData && pageQueries.some((q) => q.isLoading);

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange?.(isInitialLoading);
  }, [isInitialLoading, onLoadingChange]);

  const totalSize = rowVirtualizer.getTotalSize();

  return (
    <div
      ref={parentRef}
      className={cn('overflow-y-auto', className)}
      style={style}
    >
      <div
        className="relative flex min-h-full flex-col"
        style={{
          height: totalSize > 0 ? `${totalSize}px` : undefined,
        }}
      >
        <ul
          className={cn('relative flex-1 snap-y list-none', {
            'divide-y [&>li:last-child]:border-b!': hasDivider,
          })}
        >
          {virtualItems.map(({ index, size, start }) => {
            const item = itemsMap.get(index);
            const isLoadingRow = item === undefined;

            return (
              <li
                key={index}
                className="absolute top-0 left-0 w-full border-solid border-gray-200"
                style={{
                  height: `${size}px`,
                  transform: `translateY(${start}px)`,
                }}
              >
                {isLoadingRow
                  ? (renderLoadingItem?.(index) ?? (
                      <div className="flex h-full animate-pulse items-center rounded-sm bg-tiga-surface-off-white text-sm bg-blend-multiply" />
                    ))
                  : renderItem(item)}
              </li>
            );
          })}
        </ul>
        {isLoadingMorePages && (
          <div className="sticky right-0 bottom-0 left-0 z-20 mt-auto flex items-center justify-center">
            <div className="animate-loadingOverlay rounded-full px-4 py-2">
              <Spinner title="Lade Daten…" initialDelay={300} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
